import React, { useCallback, useEffect, useState } from "react";
import { Copy, Send, Ban, RefreshCw, Landmark, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/auth";
import { fmtDate } from "@/lib/datetime";

const STATE_LABEL = { active: "Actif", paid: "Réglé", revoked: "Révoqué", expired: "Expiré" };

function LinkRow({ l, onAction, stepLabel }) {
  return (
    <li className="border border-black/10 px-3 py-2 text-[13px] flex flex-wrap items-center justify-between gap-2" data-testid={`pay-link-${l.id}`}>
      <div>
        <span className="font-mono">{stepLabel || l.step_key}</span> · <span data-testid={`pay-link-state-${l.id}`}>{STATE_LABEL[l.state]}</span>
        <div className="text-[11px] font-mono text-neutral-500">{l.recipient_email || "non envoyé"} · expire le {fmtDate(l.expires_at)}</div>
      </div>
      {l.state === "active" && (
        <div className="flex gap-1">
          <button onClick={() => onAction(`/admin/payment-links/${l.id}/renew`, "Lien prolongé de 30 jours")} className="cs-btn-ghost !text-black !border-black !py-1 !px-2 text-[11px]" data-testid={`pay-link-renew-${l.id}`}><RefreshCw size={11} /> <span>Prolonger</span></button>
          <button onClick={() => onAction(`/admin/payment-links/${l.id}/revoke`, "Lien révoqué")} className="cs-btn-ghost !text-red-700 !border-red-700 !py-1 !px-2 text-[11px]" data-testid={`pay-link-revoke-${l.id}`}><Ban size={11} /> <span>Révoquer</span></button>
        </div>
      )}
      {l.state === "expired" && (
        <button onClick={() => onAction(`/admin/payment-links/${l.id}/renew`, "Lien prolongé de 30 jours")} className="cs-btn-ghost !text-black !border-black !py-1 !px-2 text-[11px]" data-testid={`pay-link-renew-${l.id}`}><RefreshCw size={11} /> <span>Renouveler</span></button>
      )}
    </li>
  );
}

function TransferForm({ order, pendingSteps, onDone }) {
  const [step, setStep] = useState(pendingSteps[0]?.key || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!window.confirm("Confirmez-vous la réception effective des fonds ? La facture sera émise.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/transfer`, { step_key: step, received_on: date, reference: ref });
      toast.success("Virement enregistré, facture émise");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-black/10 pt-4 mt-4" data-testid="transfer-form">
      <div className="cs-eyebrow text-neutral-500 inline-flex items-center gap-1"><Landmark size={12} /> Confirmer un virement reçu</div>
      <div className="mt-2 grid sm:grid-cols-3 gap-2">
        <select value={step} onChange={(e) => setStep(e.target.value)} className="cs-input" data-testid="transfer-step">
          {pendingSteps.map((s) => <option key={s.key} value={s.key}>{s.label} · {s.amount} €</option>)}
        </select>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="cs-input" data-testid="transfer-date" />
        <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Référence du virement" className="cs-input" data-testid="transfer-reference" />
      </div>
      <button onClick={submit} disabled={busy || !ref.trim() || !step} className="cs-btn-ghost !text-black !border-black mt-2 text-[13px]" data-testid="transfer-submit">
        {busy && <Loader2 size={12} className="animate-spin" />} <span>Enregistrer la réception</span>
      </button>
    </div>
  );
}

export function AdminEndClientPayments({ order, onReload }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [lastUrl, setLastUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const billedToEnd = order.billing_party === "end_client" && order.end_client;

  const load = useCallback(async () => {
    const { data: d } = await api.get(`/admin/orders/${order.id}/payment-links`);
    setData(d);
    if (d.end_client) {
      setEmail((v) => v || d.end_client.billing_email || "");
      setName((v) => v || `${d.end_client.first_name || ""} ${d.end_client.last_name || ""}`.trim());
    }
  }, [order.id]);
  useEffect(() => { if (billedToEnd) { load(); } }, [billedToEnd, load]);

  if (!billedToEnd || !order.payment_plan) return null;
  const pendingSteps = (order.payment_plan.steps || []).filter((s) => s.status !== "paid");

  const action = async (path, ok) => {
    try { await api.post(path); toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const create = async (send) => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/admin/orders/${order.id}/payment-links`, { email, name, message, send, step_key: pendingSteps[0]?.key });
      setLastUrl(d.url);
      toast.success(send ? (d.sent ? "Demande de règlement envoyée" : "Lien créé, mais l'e-mail n'a pas pu partir") : "Lien créé");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const refunds = (data?.transactions || []).filter((t) => t.needs_refund);

  return (
    <div className="cs-card p-6" data-testid="admin-end-client-payments">
      <div className="cs-eyebrow text-[#0E7C90]">Règlement par le client final</div>
      <p className="mt-2 text-[13px] text-neutral-600">
        Règlement attendu : <strong>{order.payer === "end_client" ? "client final" : "titulaire du compte"}</strong>.
        Les liens donnent accès uniquement à la demande de règlement (30 jours, révocables).
      </p>
      {refunds.length > 0 && (
        <div className="mt-3 border border-amber-500 bg-amber-50 px-3 py-2 text-[13px] text-amber-800 inline-flex items-center gap-2" data-testid="duplicate-payment-alert">
          <AlertTriangle size={14} /> {refunds.length} paiement(s) en double reçu(s), à rembourser depuis Stripe (aucune facture créée).
        </div>
      )}
      {pendingSteps.length > 0 && order.payer === "end_client" && (
        <div className="mt-4 space-y-2">
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du destinataire" className="cs-input" data-testid="pay-link-name" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail du destinataire" className="cs-input" data-testid="pay-link-email" />
          </div>
          <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message d'accompagnement (facultatif)" className="cs-input" data-testid="pay-link-message" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => create(true)} disabled={busy} className="cs-btn-primary text-[13px]" data-testid="pay-link-send"><Send size={12} /> Envoyer la demande ({pendingSteps[0].label})</button>
            <button onClick={() => create(false)} disabled={busy} className="cs-btn-ghost !text-black !border-black text-[13px]" data-testid="pay-link-create"><span>Créer le lien sans envoyer</span></button>
          </div>
          {lastUrl && (
            <button onClick={() => { navigator.clipboard?.writeText(lastUrl); toast.success("Lien copié"); }} className="text-[12px] font-mono underline inline-flex items-center gap-1 break-all text-left" data-testid="pay-link-copy">
              <Copy size={11} /> {lastUrl}
            </button>
          )}
        </div>
      )}
      {(data?.links || []).length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="pay-links-list">
          {data.links.map((l) => <LinkRow key={l.id} l={l} onAction={action} stepLabel={(order.payment_plan.steps || []).find((s) => s.key === l.step_key)?.label} />)}
        </ul>
      )}
      {pendingSteps.length > 0 && <TransferForm order={order} pendingSteps={pendingSteps} onDone={() => { onReload(); load(); }} />}
    </div>
  );
}
